import React from "react";
import LegalPage from "./LegalPage";

export default function Terms() {
  return (
    <LegalPage
      seoTitle="Terms and Conditions"
      seoDescription="HelloSir.ai Terms and Conditions — service agreement, user obligations, fair-use limits, billing, intellectual property and dispute resolution. A Cloud Lab Technologies (UK) product."
      seoKeywords={["hellosir.ai terms", "AI chatbot terms of service", "AI calling agent terms", "SaaS terms and conditions", "Cloud Lab Technologies"]}
      path="/terms"
      tagline="// legal"
      heading="Terms & Conditions"
    >
      <p>
        These Terms & Conditions ("Terms") govern your access to and use of <strong>HelloSir.ai</strong> ("HelloSir", "we", "us", "our"),
        a SaaS product owned and operated by <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer"><strong>Cloud Lab Technologies</strong></a>,
        a software technology company registered in the United Kingdom. By creating an account, deploying an agent or embedding the HelloSir widget on
        any website, you confirm that you have read, understood and agreed to these Terms.
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li><strong>Service:</strong> the HelloSir.ai platform, including the AI text chatbot, AI voice chatbot and AI calling agent.</li>
        <li><strong>Customer:</strong> any individual or legal entity that has registered for an account.</li>
        <li><strong>Agent:</strong> an AI-powered communication unit configured by the Customer.</li>
        <li><strong>Credits:</strong> the metered units used to pay for messages, voice minutes and call minutes.</li>
      </ul>

      <h2>2. Eligibility & account</h2>
      <p>
        You must be at least 18 years old and legally able to enter into a binding contract. You agree to provide accurate, current and complete
        information when registering and to keep your account credentials confidential. You are responsible for all activity that occurs under your account.
      </p>

      <h2>3. Acceptable use</h2>
      <p>You will not:</p>
      <ul>
        <li>use the Service for unlawful, harmful, threatening, defamatory or discriminatory purposes;</li>
        <li>train an Agent on content you do not own or are not licensed to use;</li>
        <li>impersonate a real person without that person's consent;</li>
        <li>circumvent rate-limits, security, or attempt to scrape the Service;</li>
        <li>use HelloSir to send unsolicited messages or calls in violation of TCPA, GDPR, PECR, CAN-SPAM or any applicable law;</li>
        <li>use the Service to provide medical, legal or financial advice without appropriate professional licensing.</li>
      </ul>

      <h2>4. Free trial & paid usage</h2>
      <p>
        New accounts receive a free trial that includes a limited quota of chats, voice minutes and one trained agent. Once the trial limits are reached
        you may purchase top-up packs in $9.99 increments. Credits are non-transferable, do not expire and are non-refundable except as set out in our{" "}
        <a href="/refund-policy">Refund Policy</a>.
      </p>

      <h2>5. Third-party services</h2>
      <p>
        HelloSir integrates with several third-party providers to deliver telephony, payment and natural-language services. By using the Service you also
        agree to the terms of the applicable providers. Outages, model changes or pricing changes from upstream providers may affect the Service.
      </p>

      <h2>6. Intellectual property</h2>
      <p>
        HelloSir, the HelloSir.ai brand, the platform code and all related trademarks are the property of Cloud Lab Technologies. You retain ownership of
        the content you upload, your knowledge base, your conversation logs and any text generated by your Agent on your behalf. You grant us a limited,
        non-exclusive licence to process this content solely for the purpose of delivering the Service.
      </p>

      <h2>7. Service levels</h2>
      <p>
        We target 99.5% monthly uptime, excluding scheduled maintenance and force-majeure events. The Service is provided on an "as is" basis. We do not
        guarantee that AI replies are free of errors; you remain responsible for any actions taken based on Agent output.
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, the aggregate liability of HelloSir.ai and Cloud Lab Technologies in respect of any claim arising under
        these Terms shall not exceed the total amount paid by you to us in the three (3) months preceding the event giving rise to the claim.
      </p>

      <h2>9. Termination</h2>
      <p>
        You may close your account at any time from the dashboard. We may suspend or terminate your access if you breach these Terms or engage in
        activity that endangers other users or the Service. Upon termination, your data may be retained for up to 30 days before secure deletion.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Any dispute that cannot be resolved through good-faith negotiation will be referred
        to the exclusive jurisdiction of the courts of London, United Kingdom.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions about these Terms? Email <a href="mailto:legal@hellosir.ai">legal@hellosir.ai</a> or write to{" "}
        Cloud Lab Technologies, United Kingdom — <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer">cloudlabweb.com</a>.
      </p>
    </LegalPage>
  );
}
